import { reportShell, reportOptions } from '../../reports/utils/shell';
import { fmtMoney } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';

// Documento mensual de liquidación por cliente (HTML→PDF, misma metodología que los demás
// informes: reportShell + reportOptions). Explica la liquidación del mes con el mayor detalle
// posible y el saldo a la fecha con colores intuitivos: VERDE = saldo a favor, ROJO = a pagar.
// Los montos ya vienen calculados/congelados por el backend (shares del batch, doble-partida
// del ledger); el front SOLO maqueta (decisión 0001, no deriva dinero).

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const SOURCE_LABEL = { liquidation: 'Pensión', sale: 'Venta', exchange: 'Ajuste', liquidation_adjustment: 'Ajuste', payment: 'Pago', credit_note: 'Nota crédito', reversal: 'Reversa' };

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const n = (v) => Number(v) || 0;

// Estilos scopeados al documento (no tocan el shell compartido). Prefijo .bill-.
const BILL_STYLES = `<style>
  .bill-bal { margin: 0 0 16px; padding: 14px 18px; border-radius: 12px; border: 1px solid; }
  .bill-bal__label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
  .bill-bal__value { display: block; font-size: 26px; font-weight: 800; margin: 2px 0; }
  .bill-bal__hint { display: block; font-size: 11px; opacity: 0.9; }
  .bill-bal--debt { background: #fef2f2; border-color: #fecaca; color: #b91c1c; }
  .bill-bal--credit { background: #ecfdf5; border-color: #a7f3d0; color: #047857; }
  .bill-bal--zero { background: #f8fafc; border-color: #e2e8f0; color: #334155; }
  .bill-cow-title { margin: 16px 0 6px; font-size: 12px; font-weight: 700; color: #0f172a; }
  .bill-cow-title small { color: #64748b; font-weight: 600; }
  .bill-left { text-align: left !important; }
  .bill-amt { text-align: right !important; font-variant-numeric: tabular-nums; }
  .bill-pos { color: #047857; font-weight: 700; }
  .bill-neg { color: #b91c1c; font-weight: 700; }
  .bill-grand td { background: #ecfdf5 !important; color: #065f46; font-size: 12px; }
</style>`;

// Bloque de saldo con color según signo. balance > 0 → el cliente DEBE (rojo);
// balance < 0 → saldo a FAVOR (verde); 0 → al día (neutro).
function balanceBox(balance, { debit, credit } = {}) {
    const b = n(balance);
    const mod = b > 0 ? 'debt' : b < 0 ? 'credit' : 'zero';
    const label = b > 0 ? 'Saldo a pagar' : b < 0 ? 'Saldo a favor' : 'Cuenta al día';
    const hint = (debit != null || credit != null)
        ? `Cargos totales ${fmtMoney(debit)} · Abonos totales ${fmtMoney(credit)}`
        : '';
    return `<div class="bill-bal bill-bal--${mod}">
        <span class="bill-bal__label">${label}</span>
        <span class="bill-bal__value">${fmtMoney(Math.abs(b))}</span>
        ${hint ? `<span class="bill-bal__hint">${hint}</span>` : ''}
    </div>`;
}

// Detalle de la liquidación del mes, por vaca. Muestra manejo, días, tarifa/día, pensión
// total de la línea y la parte de este cliente (según su %). Total al pie.
function detailHtml(cows, grand) {
    if (!cows.length) return '<p class="report-subtitle">Sin cargos de pensión para este cliente en el período.</p>';
    const cowsHtml = cows.map((c) => {
        const rows = (c.lines || []).map((l) => `<tr>
            <td class="bill-left">${esc(l.management || '—')}</td>
            <td>${l.days ?? '—'}</td>
            <td class="bill-amt">${fmtMoney(l.day_rate)}</td>
            <td class="bill-amt">${fmtMoney(l.pension)}</td>
            <td class="bill-amt">${fmtMoney(l.amount)}</td>
        </tr>`).join('');
        const head = `${esc(c.animal?.name || c.animal?.number || '—')}`
            + (c.animal?.number && c.animal?.name ? ` <small>· ${esc(c.animal.number)}</small>` : '')
            + (c.percent != null ? ` <small>· tu ${c.percent}%</small>` : '');
        return `<h3 class="bill-cow-title">${head}</h3>
            <table>
                <thead><tr><th class="bill-left">Manejo</th><th>Días</th><th>Tarifa/día</th><th>Pensión</th><th>Tu parte</th></tr></thead>
                <tbody>${rows || '<tr><td colspan="5">—</td></tr>'}</tbody>
                <tfoot><tr><td class="bill-left" colspan="4">Subtotal</td><td class="bill-amt">${fmtMoney(c.subtotal)}</td></tr></tfoot>
            </table>`;
    }).join('');
    return `${cowsHtml}
        <table style="margin-top:10px;"><tbody class="bill-grand"><tr>
            <td class="bill-left" style="width:70%;">Total pensión del mes</td>
            <td class="bill-amt">${fmtMoney(grand)}</td>
        </tr></tbody></table>`;
}

// Movimientos del mes (ledger, doble-partida): cargos (débito) y abonos (crédito) con fecha,
// concepto y origen. Fila de totales al pie.
function movementsHtml(movements = [], totals = {}) {
    if (!movements.length) return '<p class="report-subtitle">Sin movimientos registrados en la cuenta para este mes.</p>';
    const rows = movements.map((m) => `<tr>
        <td>${formatDateOnly(m.entry_date) || '—'}</td>
        <td class="bill-left">${esc(m.description || '—')}</td>
        <td>${esc(SOURCE_LABEL[m.source_type] || m.source_type || '—')}</td>
        <td class="bill-amt">${m.direction === 'debit' ? fmtMoney(m.amount) : ''}</td>
        <td class="bill-amt">${m.direction === 'credit' ? fmtMoney(m.amount) : ''}</td>
    </tr>`).join('');
    return `<table>
        <thead><tr><th>Fecha</th><th class="bill-left">Concepto</th><th>Origen</th><th>Cargo</th><th>Abono</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td class="bill-left" colspan="3">Totales del mes</td>
            <td class="bill-amt">${fmtMoney(totals.debit)}</td>
            <td class="bill-amt">${fmtMoney(totals.credit)}</td></tr></tfoot>
    </table>`;
}

// Resumen por concepto (by_source del mes): neto (débito − crédito) por tipo de origen.
function bySourceHtml(bySource = {}) {
    const keys = Object.keys(bySource);
    if (!keys.length) return '';
    const items = keys.map((k) => {
        const net = n(bySource[k].debit) - n(bySource[k].credit);
        const cls = net > 0 ? 'bill-neg' : net < 0 ? 'bill-pos' : '';
        return `<div class="meta-item"><strong>${esc(SOURCE_LABEL[k] || k)}</strong><span class="${cls}">${fmtMoney(net)}</span></div>`;
    }).join('');
    return `<h2 class="report-section-title">Resumen por concepto del mes</h2><div class="meta">${items}</div>`;
}

// account = { total: { debit, credit, balance }, month: { debit, credit, movements, bySource } }
export function buildClientBillReport({ client, year, month, cows, grand, account = {} }) {
    const total = account.total || {};
    const monthAcct = account.month || {};

    const body = `${BILL_STYLES}
        <h1 class="report-title">Liquidación mensual</h1>
        <p class="report-subtitle">Cliente: ${esc(client?.name || '—')} · Periodo: ${MONTHS[month - 1]} ${year}</p>

        ${balanceBox(total.balance, { debit: total.debit, credit: total.credit })}

        <div class="meta">
            <div class="meta-item"><strong>Periodo</strong><span>${MONTHS[month - 1]} ${year}</span></div>
            <div class="meta-item"><strong>Pensión del mes</strong><span>${fmtMoney(grand)}</span></div>
            <div class="meta-item"><strong>Vacas liquidadas</strong><span>${cows.length}</span></div>
        </div>

        <h2 class="report-section-title">Detalle de la liquidación</h2>
        ${detailHtml(cows, grand)}

        <h2 class="report-section-title">Movimientos del mes</h2>
        ${movementsHtml(monthAcct.movements, { debit: monthAcct.debit, credit: monthAcct.credit })}

        ${bySourceHtml(monthAcct.bySource)}

        <p style="margin-top:24px;text-align:center;font-size:9px;color:#9ca3af;line-height:1.6;">
            COWRE℠ v2 · Documento generado automáticamente<br/>
            Agropecuaria Los Manantiales S.A.S. · Nit: 901189031-9
        </p>`;

    const slug = String(client?.name || 'cliente').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return {
        html: reportShell({ bodyHtml: body }),
        filename: `liquidacion-${slug}-${year}-${String(month).padStart(2, '0')}`,
        options: reportOptions(),
    };
}
